// Used in many components

import {
    Component,
    AfterViewInit,
    Input,
    EventEmitter,
    Output,
    forwardRef,
    OnChanges,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import * as quillEditor from "ngx-quill";

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TextEditorComponent),
    multi: true,
};

@Component({
    selector: "text-editor",
    templateUrl: "./text-editor.component.html",
    styleUrls: ["./text-editor.component.scss"],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
    standalone: false
})
export class TextEditorComponent
    implements AfterViewInit, ControlValueAccessor, OnChanges
{
    rawTextValue: string;
    counter = 0; // number of characters
    counterFlag = false; // is counter over limit

    @Input() maxLength = 1000;
    @Input() required = true;
    @Input() placeholder: string;
    @Input() formControlName: string;
    @Input() text: string;

    @Output() onContentChanged = new EventEmitter<string>();
    @Output() setQuillEditorTouched = new EventEmitter<void>();

    // MRK TODO This is a dirty solution. use form validation instead.
    @Output() characterLimit = new EventEmitter<boolean>(); // sends true if character if over limit else false

    innerValue = "";
    firstOnChange = true; // this is just workaround to fix a Quill behavior of adding <p><br></p> for new entries

    modules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "underline", "strike"], // toggled buttons
            [{ header: 1 }, { header: 2 }], // custom button values
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
            [{ align: [] }],
            ["link"],
            ["clean"], // remove formatting button
        ],
    };

    constructor() {}

    ngOnChanges() {
        this.firstOnChange = true;

        if (this.text) {
            const updatedValue = this.removeStyleAttribute(this.text);
            if (updatedValue) {
                this.innerValue = updatedValue;
            }
        }

        if (this.text === null) {
            this.innerValue = "";
        }
    }

    ngAfterViewInit() {}

    // From ControlValueAccessor interface
    writeValue(value: any) {
        const updatedValue = this.removeStyleAttribute(value);
        if (updatedValue) {
            this.innerValue = updatedValue;
        }
    }

    // From ControlValueAccessor interface
    registerOnChange(fn: any) {
        this.propagateChange = fn;
    }

    // From ControlValueAccessor interface
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    // get accessor
    get value(): any {
        return this.innerValue;
    }

    // set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
        }
    }

    // propagate changes into the custom form control
    propagateChange = (_: any) => {};

    onTouched = () => {};

    onChange(editor: quillEditor.ContentChange) {
        this.onTouched();

        // set changed value
        if (this.firstOnChange) {
            // Check for weird edge case, onInit quill adds <p><br></p> between <p><ol> tags
            // creating an infinite loop
            if (editor.html) {
                const htmlSplit = editor.html.split("<p><br></p>");
                if (htmlSplit.length > 1) {
                    // edge case found, let string equal previous string
                    editor.html = this.removeStyleAttribute(this.innerValue);
                }
            }

            this.firstOnChange = false;
        }
        const updatedValue = this.removeStyleAttribute(editor.html);
        this.innerValue = updatedValue;

        this.rawTextValue = editor.text;
        this.setCounter();

        // propagate value into form control using control value accessor interface
        this.propagateChange(this.innerValue);

        this.onContentChanged.emit(this.innerValue);
    }

    quillEditorTouched() {
        this.setQuillEditorTouched.emit();
    }

    setCounter() {
        const text = this.rawTextValue.trim();
        this.counter = text.length;
        this.counterFlag = this.counter > this.maxLength;
    }

    // Quill Editor will go into infinite loop mode with any style attributes so remove them
    removeStyleAttribute(text: string | undefined | null): string {
        if (text) {
            const styleRegex = /\sstyle="[A-z0-9:\s\-\;\(\),]*"/gm;
            return text.replace(styleRegex, "");
        } else {
            return ""; //Previously, this was just returning an undefined if you passed in something falsy
        }
    }
}
